import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { motion } from 'motion/react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ApiError, NetworkError } from '@/shared/api/http';
import { signUp, useSession } from '@/shared/auth';
import { springs } from '@/shared/lib/motion';
import { t } from '@/shared/i18n';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';

import styles from '../LoginPage/LoginPage.module.scss';

const schema = z.object({
  name: z.string().trim().min(1, t.register.nameRequired),
  email: z.email(t.login.invalidEmail),
  password: z.string().min(8, t.login.passwordTooShort),
});

type FormValues = z.infer<typeof schema>;

/**
 * Регистрация без приглашения. После неё человек сразу внутри, но без
 * пространств: его находят по почте и приглашают, а приглашение ждёт его
 * на главной.
 */
export function RegisterPage() {
  const session = useSession();
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', password: '' },
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      await signUp(values.email, values.name, values.password);
      await navigate({ to: '/app' });
    } catch (cause) {
      const field = cause instanceof ApiError && cause.status === 409 ? 'email' : 'password';
      const message =
        cause instanceof ApiError
          ? cause.detail
          : cause instanceof NetworkError
            ? t.common.networkError
            : t.common.unknownError;

      form.setError(field, { message });
    }
  });

  if (session.status === 'authenticated') {
    return (
      <div className={styles['card']}>
        <p className={styles['note']}>
          {t.login.alreadyIn} <b>{session.user.email}</b>.
        </p>
        <Button asChild iconRight={<ArrowRight />}>
          <Link to="/app">{t.login.goHome}</Link>
        </Button>
      </div>
    );
  }

  return (
    <motion.form
      className={styles['card']}
      onSubmit={(event) => {
        void submit(event);
      }}
      noValidate
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={springs.spring}
    >
      <header className={styles['head']}>
        <h1 className={styles['title']}>{t.register.title}</h1>
        <p className={styles['subtitle']}>{t.register.subtitle}</p>
      </header>

      <Field label={t.register.name} required error={form.formState.errors.name?.message}>
        <Input
          autoComplete="name"
          placeholder={t.register.namePlaceholder}
          autoFocus
          {...form.register('name')}
        />
      </Field>

      <Field label={t.login.email} required error={form.formState.errors.email?.message}>
        <Input
          type="email"
          autoComplete="username"
          placeholder="you@flow-do.ru"
          {...form.register('email')}
        />
      </Field>

      <Field label={t.login.password} required error={form.formState.errors.password?.message}>
        <Input type="password" autoComplete="new-password" {...form.register('password')} />
      </Field>

      <Button
        type="submit"
        size="lg"
        fullWidth
        loading={form.formState.isSubmitting}
        iconRight={<ArrowRight />}
      >
        {t.register.submit}
      </Button>

      <p className={styles['switch']}>
        {t.register.haveAccount}{' '}
        <Link to="/login" search={{}} className={styles['switch__link']}>
          {t.register.login}
        </Link>
      </p>
    </motion.form>
  );
}
